import { Star, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { useRef } from 'react';
import { WHATSAPP_URL } from '../constants';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useCountUp } from '../hooks/useCountUp';

const GOOGLE_REVIEWS_URL = 'https://share.google/BljsumTZh1DHam1jU';

function StatCard({ value, label, numeric, suffix, index }) {
  const [ref, count] = useCountUp(numeric || 0);
  return (
    <div
      data-testid={`stat-card-${index + 1}`}
      className="bg-gradient-to-br from-[#0D47A1] to-[#00897B] rounded-2xl p-6 text-center text-white"
    >
      <div ref={numeric ? ref : undefined} className="font-poppins font-bold text-3xl mb-1">
        {numeric ? `${count}${suffix}` : value}
      </div>
      <div className="text-white/75 text-sm">{label}</div>
    </div>
  );
}


const googleReviews = [
{
  name: 'Elane Gomes',
  initial: 'E',
  color: '#0F9D58',
  time: 'há 6 meses',
  stars: 5,
  text: 'Médico de excelência. Super recomendo. Meu filho fez a cirurgia de simpatectomia e foi tudo perfeito. Esclareceu todas as dúvidas e nos deixou com muita segurança para a realização do procedimento.'
},
{
  name: 'Fabrício Silva',
  initial: 'F',
  color: '#4285F4',
  time: 'há 1 mês',
  stars: 5,
  text: 'Doutor excelente, recomendo com toda certeza. Equipe muito bem preparada, doutor muito atencioso. Parabéns.'
},
{
  name: 'Ana Rita',
  initial: 'A',
  color: '#EA4335',
  time: 'há 2 meses',
  stars: 5,
  text: 'Não tenho do que reclamar, somente agradecer por todo cuidado e atenção dedicados a mim não somente no momento cirúrgico, mas principalmente durante todo o pós operatório! Profissional de excelência e equipe maravilhosa!'
},
{
  name: 'Gabriel',
  initial: 'G',
  color: '#795548',
  time: 'há 3 semanas',
  stars: 5,
  text: 'Excelente profissional, humano e competente. Recomendo.'
}];


function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>);

}

function StarRating({ count }) {
  return (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, i) =>
      <Star key={i} size={14} className={i < count ? 'text-[#FBBC05] fill-[#FBBC05]' : 'text-gray-300'} />
      )}
    </div>);

}

export default function TestimonialsSection() {
  const [ref, isVisible] = useScrollReveal();
  const scrollRef = useRef(null);

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 320, behavior: 'smooth' });
    }
  };

  return (
    <section id="depoimentos" ref={ref} className={`py-24 md:py-32 bg-white transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">

        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center bg-[#00897B]/10 text-[#00897B] rounded-full px-4 py-1.5 text-sm font-semibold mb-4">Depoimentos

          </div>
          <h2 className="font-poppins font-bold text-3xl sm:text-4xl text-[#212121] mb-6 leading-tight">
            Histórias de Quem Já se Livrou do Suor
          </h2>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          <StatCard value="150+" label="Procedimentos realizados" numeric={150} suffix="+" index={0} />
          <StatCard value="95%"  label="Taxa de satisfação"       numeric={95}  suffix="%" index={1} />
          <StatCard value="Convênio" label="e particular"         index={2} />
          <StatCard value="3-5 dias" label="Recuperação rápida"   index={3} />
        </div>

        {/* Google Reviews Carousel */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <GoogleIcon />
              <span className="font-poppins font-semibold text-[#212121] text-sm">Avaliações Google</span>
              <div className="flex items-center gap-1 ml-2">
                {[...Array(5)].map((_, i) =>
                <Star key={i} size={13} className="text-[#FBBC05] fill-[#FBBC05]" />
                )}
                <span className="font-bold text-[#212121] text-sm ml-1">5.0</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => scroll(-1)}
                data-testid="reviews-scroll-left"
                className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-[#212121]/60 hover:border-[#0D47A1] hover:text-[#0D47A1] transition-colors">

                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => scroll(1)}
                data-testid="reviews-scroll-right"
                className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-[#212121]/60 hover:border-[#0D47A1] hover:text-[#0D47A1] transition-colors">

                <ChevronRight size={18} />
              </button>
            </div>
          </div>

          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto pb-3 scroll-smooth"
            style={{ scrollSnapType: 'x mandatory', msOverflowStyle: 'none', scrollbarWidth: 'none' }}>

            {googleReviews.map((review, i) =>
            <div
              key={i}
              data-testid={`google-review-card-${i + 1}`}
              className="flex-shrink-0 w-72 bg-white border border-gray-100 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow"
              style={{ scrollSnapAlign: 'start' }}>

                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-white font-poppins font-bold text-sm flex-shrink-0"
                    style={{ backgroundColor: review.color }}>

                      {review.initial}
                    </div>
                    <div>
                      <div className="font-poppins font-semibold text-[#212121] text-sm leading-tight">{review.name}</div>
                      <div className="text-[#212121]/45 text-xs">{review.time}</div>
                    </div>
                  </div>
                  <GoogleIcon />
                </div>
                <StarRating count={review.stars} />
                <p className="text-[#212121]/70 text-sm leading-relaxed mt-3">
                  "{review.text}"
                </p>
              </div>
            )}

            {/* View more card */}
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="view-more-reviews-card"
              className="flex-shrink-0 w-64 border-2 border-dashed border-[#0D47A1]/30 rounded-2xl p-5 flex flex-col items-center justify-center gap-3 hover:border-[#0D47A1] hover:bg-[#0D47A1]/5 transition-all group"
              style={{ scrollSnapAlign: 'start' }}>

              <GoogleIcon />
              <span className="font-poppins font-semibold text-[#0D47A1] text-sm text-center group-hover:underline">
                Ver todas as avaliações no Google
              </span>
              <ExternalLink size={16} className="text-[#0D47A1]/60" />
            </a>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <p className="text-[#212121]/60 text-sm mb-4">Transforme também a sua história.</p>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="testimonials-cta-button"
            className="inline-flex items-center gap-2 bg-[#0D47A1] text-white font-poppins font-bold px-10 py-4 rounded-full hover:bg-[#00897B] transition-colors duration-200 shadow-lg hover:-translate-y-0.5">

            AGENDAR MINHA CONSULTA
          </a>
        </div>

      </div>
    </section>);

}